import { Button, Drawer, Typography } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import "./referralreport.css";
import FormatDate from "../../../../../components/FormatDate/FormatDate";
import { useStateGlobalContext } from "../../../../../contexts/GlobalContext";
import { useParams } from "react-router-dom";
import Notification from "../../../../../components/Notification/Notification";
import FormatDateTime from "../../../../../components/FormatDate/FormatDateTime";
import {
  fullAddress,
  fullName,
} from "../../../../../components/GlobalComponents/GlobalComponents";
import dayjs from "dayjs";

const { Text, Title } = Typography;

const sectionTitleStyle = {
  display: "block",
  color: "#9ca3af",
  textTransform: "uppercase",
  fontSize: "9px",
  fontWeight: "bold",
  letterSpacing: "0.1em",
  marginBottom: "12px",
  borderBottom: "1px solid #e5e7eb",
  paddingBottom: "4px",
};

const cardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  padding: "20px",
  backgroundColor: "#f9fafb",
};

const labelStyle = {
  fontWeight: "bold",
  color: "#6b7280",
  width: "110px",
  flexShrink: 0,
  textTransform: "uppercase",
  fontSize: "8px",
};

const valueStyle = { flex: 1, color: "#374151" };

const FieldRow = ({ label, children }) => (
  <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "6px" }}>
    <div style={labelStyle}>{label}:</div>
    <div style={valueStyle}>{children || "N/A"}</div>
  </div>
);

const ReferralReport = ({ visible, onClose, data }) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const {
    officeSetting,
    getOfficeSettings,
    patientBasicInfo,
    getPatientData,
    patientAllergies,
    getAllergies,
  } = useStateGlobalContext();

  const getVal = (field) => (Array.isArray(field) ? field[0] : field);
  const isVisible = (field) => (Array.isArray(field) ? field[1] === true : true);

  const patientName = fullName(patientBasicInfo);
  const patientAddr = fullAddress(patientBasicInfo);
  const patientGender =
    patientBasicInfo?.sex === "other"
      ? patientBasicInfo?.other_Gender
      : patientBasicInfo?.gender;
  const allergiesList =
    patientAllergies?.map((a) => a?.allergy_value).filter(Boolean).join(", ") || "None";

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([getOfficeSettings(), getPatientData(id), getAllergies(id)]);
      } catch (error) {
        Notification.error("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handlePrint = () => {
    const clinicName = getVal(officeSetting?.facility_name) || "Medical Facility";
    const clinicAddress =
      [
        getVal(officeSetting?.facility_address),
        getVal(officeSetting?.city),
        getVal(officeSetting?.state),
        getVal(officeSetting?.zipcode),
      ]
        .filter(Boolean)
        .join(", ") || "Address not available";

    const pName =
      [patientBasicInfo?.first_name, patientBasicInfo?.middle_name, patientBasicInfo?.last_name]
        .filter(Boolean)
        .join(" ") || "N/A";
    const pDOB = patientBasicInfo?.date_of_birth
      ? dayjs(patientBasicInfo.date_of_birth).format("MM/DD/YYYY")
      : "N/A";
    const pGender =
      patientBasicInfo?.sex === "other"
        ? patientBasicInfo?.other_Gender
        : patientBasicInfo?.gender || "N/A";
    const pAddress =
      [
        patientBasicInfo?.current_address_line_1,
        patientBasicInfo?.current_address_line_2,
        patientBasicInfo?.current_city,
        patientBasicInfo?.current_state,
        patientBasicInfo?.current_country,
        patientBasicInfo?.current_zipcode,
      ]
        .filter(Boolean)
        .join(", ") || "N/A";
    const pPhone = patientBasicInfo?.patient_phone || "N/A";
    const pEmail = patientBasicInfo?.patient_email || "N/A";
    const printAllergies =
      patientAllergies?.map((a) => a?.allergy_value).filter(Boolean).join(", ") || "None";

    const billingDataHtml = [
      officeSetting?.additional_basic_data_1,
      officeSetting?.additional_basic_data_2,
      officeSetting?.additional_basic_data_3,
    ]
      .map((field) => {
        if (!isVisible(field)) return "";
        return `<div style="display:flex;"><span style="font-weight:bold;width:60px;font-size:9px;text-transform:uppercase;color:#6b7280;">${field?.title || "Data"}:</span><span style="flex:1;">${getVal(field)}</span></div>`;
      })
      .join("");

    const referralDate = data?.date_time
      ? dayjs(data.date_time).format("MM/DD/YYYY hh:mm A")
      : "N/A";
    const startDate = data?.start_date ? dayjs(data.start_date).format("MM/DD/YYYY") : "N/A";
    const endDate = data?.end_date ? dayjs(data.end_date).format("MM/DD/YYYY") : "N/A";

    const fieldRow = (label, value) =>
      `<div style="display:flex;align-items:flex-start;margin-bottom:6px;">
        <div style="font-weight:bold;color:#6b7280;width:110px;flex-shrink:0;text-transform:uppercase;font-size:8px;">${label}:</div>
        <div style="flex:1;color:#374151;font-size:11px;">${value || "N/A"}</div>
       </div>`;

    const printHtml = `
      <html>
        <head>
          <title>Referral Report - ${data?.referral_id || "N/A"}</title>
          <style>
            body { font-family: 'Times New Roman', serif; padding: 30px; color: black; background: white; line-height: 1.4; }
            .header { display: flex; justify-content: space-between; gap: 20px; margin-bottom: 30px; align-items: stretch; }
            .box { border: 1px solid #d1d5db; border-radius: 8px; padding: 15px; background: #f9fafb; flex: 1; display: flex; flex-direction: column; }
            .box-title { color: #9ca3af; font-size: 9px; text-transform: uppercase; font-weight: bold; letter-spacing: 0.1em; margin-bottom: 8px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; display: block; }
            .title-section { border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: flex-end; }
            .two-col { display: flex; gap: 20px; margin-bottom: 20px; }
            .section { border: 1px solid #d1d5db; border-radius: 8px; padding: 15px; background: #f9fafb; margin-bottom: 20px; }
            @media print { body { padding: 0; } @page { margin: 12mm; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="box" style="max-width:58%;">
              <span class="box-title">Facility Information</span>
              <h2 style="margin:0 0 10px 0;font-size:18px;color:#000;">${clinicName}</h2>
              <div style="font-family:sans-serif;font-size:11px;color:#374151;display:flex;flex-direction:column;gap:4px;">
                <div style="display:flex;"><span style="font-weight:bold;width:60px;font-size:9px;text-transform:uppercase;color:#6b7280;">Address:</span><span style="flex:1;">${clinicAddress}</span></div>
                <div style="display:flex;"><span style="font-weight:bold;width:60px;font-size:9px;text-transform:uppercase;color:#6b7280;">Phone:</span><span style="flex:1;">${getVal(officeSetting?.office_phone) || "N/A"}</span></div>
                <div style="display:flex;"><span style="font-weight:bold;width:60px;font-size:9px;text-transform:uppercase;color:#6b7280;">Fax:</span><span style="flex:1;">${getVal(officeSetting?.office_fax) || "N/A"}</span></div>
                <div style="display:flex;"><span style="font-weight:bold;width:60px;font-size:9px;text-transform:uppercase;color:#6b7280;">Email:</span><span style="flex:1;">${getVal(officeSetting?.buisness_email) || "N/A"}</span></div>
                ${billingDataHtml}
              </div>
            </div>
            <div class="box" style="max-width:40%;text-align:right;">
              <span class="box-title">Patient Information</span>
              <div style="font-family:sans-serif;font-size:14px;font-weight:bold;color:#111;margin-bottom:6px;">${pName}</div>
              <div style="font-family:sans-serif;font-size:11px;color:#374151;display:flex;flex-direction:column;gap:3px;">
                <div><span style="font-weight:bold;color:#6b7280;text-transform:uppercase;font-size:8px;">DOB:</span> ${pDOB} &nbsp;|&nbsp; <span style="font-weight:bold;color:#6b7280;text-transform:uppercase;font-size:8px;">Gender:</span> ${pGender}</div>
                <div>${pAddress}</div>
                <div style="font-weight:bold;">${pPhone}</div>
                <div>${pEmail}</div>
                <div style="margin-top:4px;"><span style="font-weight:bold;color:#ef4444;">Allergies:</span> <span style="color:#ef4444;">${printAllergies}</span></div>
              </div>
            </div>
          </div>

          <div class="title-section">
            <div>
              <h2 style="margin:0;text-transform:uppercase;">Referral Report</h2>
              <div style="font-size:12px;font-family:monospace;color:#666;margin-top:4px;">ID: ${data?.referral_id || "N/A"}</div>
            </div>
            <div style="text-align:right;font-weight:bold;color:${data?.status === "Cancelled" ? "#ef4444" : "#374151"};">
              Status: ${data?.status || "N/A"}
            </div>
          </div>

          <div class="two-col">
            <div class="section" style="flex:1;">
              <span class="box-title">Referring From</span>
              ${fieldRow("Name", data?.referral_from_name)}
              ${fieldRow("Speciality", data?.referral_from_speciality)}
              ${fieldRow("Phone", data?.referral_from_phone)}
              ${fieldRow("Fax", data?.referral_from_fax)}
              ${fieldRow("Email", data?.referral_from_email)}
            </div>
            <div class="section" style="flex:1;">
              <span class="box-title">Referral Dates</span>
              ${fieldRow("Referral Date", referralDate)}
              ${fieldRow("Start Date", startDate)}
              ${fieldRow("Expiry Date", endDate)}
              ${fieldRow("Auth Code", data?.auth_code)}
            </div>
          </div>

          <div class="section">
            <span class="box-title">Patient Insurance Information</span>
            <div style="display:flex;gap:40px;">
              <div style="flex:1;">
                ${fieldRow("Type", data?.insurance_type)}
                ${fieldRow("Company", data?.insurance_company)}
                ${fieldRow("Plan", data?.insurance_plan)}
              </div>
              <div style="flex:1;">
                ${fieldRow("Group #", data?.insurance_group)}
                ${fieldRow("Policy #", data?.insurance_policy)}
              </div>
            </div>
          </div>

          <div class="section">
            <span class="box-title">Referring To</span>
            <div style="display:flex;gap:40px;">
              <div style="flex:1;">
                ${fieldRow("Full Name", data?.referral_to_full_name)}
                ${fieldRow("Speciality", data?.referral_to_speciality)}
                ${fieldRow("Phone", data?.referral_to_phone)}
                ${fieldRow("Fax", data?.referral_to_fax)}
              </div>
              <div style="flex:1;">
                ${fieldRow("Direct Email", data?.referral_to_email)}
                ${fieldRow("Full Address", data?.referral_to_address)}
                ${fieldRow("Operating Hours", data?.referral_to_operating_hours)}
              </div>
            </div>
          </div>

          <div class="section">
            <span class="box-title">Clinical Notes</span>
            ${fieldRow("Reason for Referral", data?.reason_for_referral)}
            ${fieldRow("ICD-10 Codes", data?.patient_diagnostic_code)}
            ${fieldRow("Clinical Notes", data?.clinic_notes)}
          </div>
        </body>
      </html>
    `;

    let iframe = document.getElementById("referral-print-iframe");
    if (!iframe) {
      iframe = document.createElement("iframe");
      iframe.id = "referral-print-iframe";
      iframe.style.position = "absolute";
      iframe.style.top = "-9999px";
      iframe.style.left = "-9999px";
      document.body.appendChild(iframe);
    }

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(printHtml);
    iframeDoc.close();

    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }, 500);
  };

  return (
    <Drawer
      title="Referral Report"
      placement="right"
      width="900px"
      onClose={onClose}
      open={visible}
      extra={
        <Button
          type="primary"
          icon={<PrinterOutlined />}
          onClick={handlePrint}
          style={{ backgroundColor: "#0ea5e9", borderColor: "#0ea5e9" }}
        >
          Print Now
        </Button>
      }
    >
      <div className="bg-white">
        <div className="p-8 text-black font-serif" style={{ color: "black" }}>

          {/* HEADER — Facility + Patient (mirrors LabPrescription exactly) */}
          <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: "24px", marginBottom: "16px", gap: "20px", alignItems: "stretch" }}>

            {/* FACILITY BOX */}
            <div style={{ maxWidth: "58%", flex: 1, ...cardStyle }}>
              <Text style={sectionTitleStyle}>Facility Information</Text>
              {officeSetting && (
                <div className="text-left font-sans text-black">
                  <Title level={4} style={{ margin: 0, color: "black", fontFamily: "serif", fontWeight: "bold", letterSpacing: "-0.025em", marginBottom: "12px" }}>
                    {getVal(officeSetting.facility_name)}
                  </Title>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "11px" }}>
                    {isVisible(officeSetting.facility_address) && (
                      <div style={{ display: "flex", alignItems: "flex-start" }}>
                        <div style={labelStyle}>Address:</div>
                        <div style={valueStyle}>
                          {[getVal(officeSetting.facility_address), getVal(officeSetting.city), getVal(officeSetting.state), getVal(officeSetting.zipcode)].filter(Boolean).join(", ")}
                        </div>
                      </div>
                    )}
                    {isVisible(officeSetting.office_phone) && (
                      <div style={{ display: "flex", alignItems: "flex-start" }}>
                        <div style={labelStyle}>Phone:</div>
                        <div style={valueStyle}>{getVal(officeSetting.office_phone)}</div>
                      </div>
                    )}
                    {isVisible(officeSetting.office_fax) && (
                      <div style={{ display: "flex", alignItems: "flex-start" }}>
                        <div style={labelStyle}>Fax:</div>
                        <div style={valueStyle}>{getVal(officeSetting.office_fax)}</div>
                      </div>
                    )}
                    {isVisible(officeSetting.buisness_email) && (
                      <div style={{ display: "flex", alignItems: "flex-start" }}>
                        <div style={labelStyle}>Email:</div>
                        <div style={valueStyle}>{getVal(officeSetting.buisness_email)}</div>
                      </div>
                    )}
                    {["additional_basic_data_1", "additional_basic_data_2", "additional_basic_data_3"].map((key) => {
                      const field = officeSetting[key];
                      if (!isVisible(field)) return null;
                      return (
                        <div key={key} style={{ display: "flex", alignItems: "flex-start" }}>
                          <div style={labelStyle}>{field?.title || "Data"}:</div>
                          <div style={valueStyle}>{getVal(field)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* PATIENT BOX */}
            <div style={{ maxWidth: "40%", flex: 1, display: "flex", flexDirection: "column", ...cardStyle, textAlign: "right" }}>
              <Text style={sectionTitleStyle}>Patient Information</Text>
              <div style={{ fontFamily: "sans-serif", fontSize: "12px", display: "flex", flexDirection: "column", gap: "4px", color: "black" }}>
                <div style={{ fontWeight: "bold", fontSize: "15px", textTransform: "uppercase", lineHeight: 1.25, marginBottom: "6px", color: "#111827" }}>
                  {patientName}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", fontSize: "10px" }}>
                  <span><span style={{ fontWeight: "bold", color: "#9ca3af", textTransform: "uppercase", marginRight: "4px", fontSize: "8px" }}>DOB:</span><FormatDate date={patientBasicInfo?.date_of_birth} /></span>
                  <span><span style={{ fontWeight: "bold", color: "#9ca3af", textTransform: "uppercase", marginRight: "4px", fontSize: "8px" }}>Gender:</span>{patientGender}</span>
                </div>
                <div style={{ fontSize: "11px", color: "#4b5563", lineHeight: 1.25, marginTop: "4px" }}>{patientAddr || "N/A"}</div>
                <div style={{ fontSize: "11px", fontWeight: "bold", color: "#111827" }}>{patientBasicInfo?.patient_phone || "N/A"}</div>
                <div style={{ fontSize: "11px", color: "#4b5563" }}>{patientBasicInfo?.patient_email || "N/A"}</div>
              </div>
              <div style={{ flex: 1 }} />
              {patientAllergies?.length > 0 && (
                <div style={{ paddingTop: "10px", borderTop: "1px solid #e5e7eb", marginTop: "auto" }}>
                  <Text style={{ display: "block", color: "#9ca3af", textTransform: "uppercase", fontSize: "8px", fontWeight: "bold", letterSpacing: "0.1em", marginBottom: "4px" }}>
                    Allergies
                  </Text>
                  <div style={{ fontSize: "11px", color: "#ef4444", fontWeight: "bold" }}>{allergiesList}</div>
                </div>
              )}
            </div>
          </div>

          {/* REFERRAL ORDER HEADER */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "24px", borderBottom: "1px solid #d1d5db", paddingBottom: "8px" }}>
            <div>
              <Title level={4} style={{ margin: 0, textTransform: "uppercase" }}>Referral Report</Title>
              <Text type="secondary" style={{ fontSize: "12px", fontFamily: "monospace" }}>
                ID: {data?.referral_id}
              </Text>
            </div>
            <Text strong style={{ color: data?.status === "Cancelled" ? "#ef4444" : undefined }}>
              Status: {data?.status}
            </Text>
          </div>

          {/* REFERRING FROM + DATES — two columns */}
          <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
            <div style={{ flex: 1, ...cardStyle, marginBottom: 0 }}>
              <Text style={sectionTitleStyle}>Referring From</Text>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "11px" }}>
                <FieldRow label="Name">{data?.referral_from_name}</FieldRow>
                <FieldRow label="Speciality">{data?.referral_from_speciality}</FieldRow>
                <FieldRow label="Phone">{data?.referral_from_phone}</FieldRow>
                <FieldRow label="Fax">{data?.referral_from_fax}</FieldRow>
                <FieldRow label="Email">{data?.referral_from_email}</FieldRow>
              </div>
            </div>
            <div style={{ flex: 1, ...cardStyle, marginBottom: 0 }}>
              <Text style={sectionTitleStyle}>Referral Dates</Text>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "11px" }}>
                <FieldRow label="Referral Date"><FormatDateTime date={data?.date_time} type="datetime" /></FieldRow>
                <FieldRow label="Start Date"><FormatDate date={data?.start_date} /></FieldRow>
                <FieldRow label="Expiry Date"><FormatDate date={data?.end_date} /></FieldRow>
                <FieldRow label="Auth Code">{data?.auth_code}</FieldRow>
              </div>
            </div>
          </div>

          {/* INSURANCE */}
          <div style={{ ...cardStyle, marginBottom: "20px" }}>
            <Text style={sectionTitleStyle}>Patient Insurance Information</Text>
            <div style={{ display: "flex", gap: "40px", fontSize: "11px" }}>
              <div style={{ flex: 1 }}>
                <FieldRow label="Type">{data?.insurance_type}</FieldRow>
                <FieldRow label="Company">{data?.insurance_company}</FieldRow>
                <FieldRow label="Plan">{data?.insurance_plan}</FieldRow>
              </div>
              <div style={{ flex: 1 }}>
                <FieldRow label="Group #">{data?.insurance_group}</FieldRow>
                <FieldRow label="Policy #">{data?.insurance_policy}</FieldRow>
              </div>
            </div>
          </div>

          {/* REFERRING TO */}
          <div style={{ ...cardStyle, marginBottom: "20px" }}>
            <Text style={sectionTitleStyle}>Referring To</Text>
            <div style={{ display: "flex", gap: "40px", fontSize: "11px" }}>
              <div style={{ flex: 1 }}>
                <FieldRow label="Full Name">{data?.referral_to_full_name}</FieldRow>
                <FieldRow label="Speciality">{data?.referral_to_speciality}</FieldRow>
                <FieldRow label="Phone">{data?.referral_to_phone}</FieldRow>
                <FieldRow label="Fax">{data?.referral_to_fax}</FieldRow>
              </div>
              <div style={{ flex: 1 }}>
                <FieldRow label="Direct Email">{data?.referral_to_email}</FieldRow>
                <FieldRow label="Full Address">{data?.referral_to_address}</FieldRow>
                <FieldRow label="Operating Hours">{data?.referral_to_operating_hours}</FieldRow>
              </div>
            </div>
          </div>

          {/* CLINICAL NOTES */}
          <div style={{ ...cardStyle, marginBottom: 0 }}>
            <Text style={sectionTitleStyle}>Clinical Notes</Text>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "11px" }}>
              <FieldRow label="Reason for Referral">{data?.reason_for_referral}</FieldRow>
              <FieldRow label="ICD-10 Codes">{data?.patient_diagnostic_code}</FieldRow>
              <FieldRow label="Clinical Notes">{data?.clinic_notes}</FieldRow>
            </div>
          </div>

        </div>
      </div>
    </Drawer>
  );
};

export default ReferralReport;
